import { Box, Chip, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import {
  formatToolCallArgumentLabel,
  formatToolCallScalar,
  formatToolCallValue,
  isRecord,
  parseToolCallArguments,
} from './toolCallCard.utils';

const MAX_RENDER_DEPTH = 3;
const MAX_VISIBLE_ARRAY_ITEMS = 12;

interface ToolCallArgumentsProps {
  rawValue: string;
}

interface ArgumentValueProps {
  value: unknown;
  depth: number;
}

const RawValue: React.FC<{ value: unknown }> = ({ value }) => {
  const { classes } = useStyles();

  return (
    <Typography component="pre" className={classes.rawValue}>
      {formatToolCallValue(JSON.stringify(value))}
    </Typography>
  );
};

const ArgumentValue: React.FC<ArgumentValueProps> = ({ value, depth }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  if (depth >= MAX_RENDER_DEPTH && (isRecord(value) || Array.isArray(value))) {
    return <RawValue value={value} />;
  }

  if (isRecord(value)) {
    return (
      <Stack spacing={0.75} className={classes.nestedObject}>
        {Object.entries(value).map(([key, childValue]) => (
          <ArgumentField
            key={key}
            label={formatToolCallArgumentLabel(key)}
            value={childValue}
            depth={depth + 1}
          />
        ))}
      </Stack>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <Typography className={classes.value}>—</Typography>;

    const visibleValues = value.slice(0, MAX_VISIBLE_ARRAY_ITEMS);
    const hasOnlyScalars = visibleValues.every((item) => !isRecord(item) && !Array.isArray(item));

    if (hasOnlyScalars) {
      return (
        <Stack direction="row" flexWrap="wrap" gap={0.75} className={classes.array}>
          {visibleValues.map((item, index) => (
            <Chip
              key={`${String(item)}-${index}`}
              label={formatToolCallScalar(item, t)}
              size="small"
              variant="outlined"
              className={classes.scalarChip}
            />
          ))}
          {value.length > MAX_VISIBLE_ARRAY_ITEMS && (
            <Typography variant="caption" className={classes.moreScalars}>
              {t('campaignPage.toolCall.more', { count: value.length - MAX_VISIBLE_ARRAY_ITEMS })}
            </Typography>
          )}
        </Stack>
      );
    }

    return (
      <Stack spacing={0.75} className={classes.array}>
        {visibleValues.map((item, index) => (
          <ArgumentField
            key={index}
            label={t('campaignPage.toolCall.item', { index: index + 1 })}
            value={item}
            depth={depth + 1}
          />
        ))}
        {value.length > MAX_VISIBLE_ARRAY_ITEMS && (
          <Typography variant="caption" className={classes.secondaryText}>
            {t('campaignPage.toolCall.moreItems', {
              count: value.length - MAX_VISIBLE_ARRAY_ITEMS,
            })}
          </Typography>
        )}
      </Stack>
    );
  }

  return <Typography className={classes.value}>{formatToolCallScalar(value, t)}</Typography>;
};

const ArgumentField: React.FC<ArgumentValueProps & { label: string }> = ({
  label,
  value,
  depth,
}) => {
  const { classes } = useStyles();

  return (
    <Box>
      <Typography variant="caption" className={classes.fieldLabel}>
        {label}
      </Typography>
      <ArgumentValue value={value} depth={depth} />
    </Box>
  );
};

export const ToolCallArguments: React.FC<ToolCallArgumentsProps> = ({ rawValue }) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const parsedValue = parseToolCallArguments(rawValue);

  return (
    <Stack spacing={1}>
      {isRecord(parsedValue) ? (
        Object.keys(parsedValue).length > 0 ? (
          Object.entries(parsedValue).map(([key, value]) => (
            <ArgumentField
              key={key}
              label={formatToolCallArgumentLabel(key)}
              value={value}
              depth={0}
            />
          ))
        ) : (
          <Typography className={cx(classes.value, classes.secondaryText)}>
            {t('campaignPage.toolCall.noArguments')}
          </Typography>
        )
      ) : (
        <ArgumentValue value={parsedValue} depth={0} />
      )}
      <Box component="details" className={classes.rawDetails}>
        <Box component="summary" className={classes.rawSummary}>
          {t('campaignPage.toolCall.viewRawJson')}
        </Box>
        <Box component="pre" className={classes.rawJson}>
          {formatToolCallValue(rawValue)}
        </Box>
      </Box>
    </Stack>
  );
};
